import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";

// components
import WhisperSTTComponent from "./WhisperSTTComponent";
import TextToSpeech from "./TextToSpeech";

// Effects
import { Waves } from "@components/EngageApp/ChatVoice";

//Video
import video1 from "@video/video-1.webm";
import video2 from "@video/video-2.webm";
import video1Mov from "@video/video-1.mov";
import video2Mov from "@video/video-2.mov";

//Image
import imgBackground from '@images/chatvoice/bg-engage.svg';
import imgBackgroundMobile from '@images/chatvoice/bg-engage-mobile.svg';

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const FrameChatFullBox = styled.div`
  position: relative;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  background-image: url(${imgBackground});
  background-repeat: repeat-x;
  background-position: center;
  background-size: cover;
  @media (max-width: 768px) {
    background-image: url(${imgBackgroundMobile});
  }
`;

/* ─── Phần video + transcript ───────────────────────────────────────────── */
const ChatWrapper = styled.div`
  display: flex;
  flex: 1;
  gap: 20px;
  overflow: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Video bên trái 40%
const VideoWrapper = styled.div`
  flex: 0 0 40%;
  height: 100%;
  position: relative;
  overflow: hidden;
  pointer-events: none;
  video::-webkit-media-controls {
    display: none;
  }
  @media (max-width: 768px) {
    width: 100%;
    aspect-ratio: auto;
    min-height: 400px;
    z-index: -1;
    video {
      transform: scale(1.5);
      outline: none; 
      &:focus,
      &:active {
        outline: none;
      }
    }
  }
`;

const TranscriptWrapperContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;  
  height: 100%;
  padding: 10px;
  max-height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  padding-bottom: 163px;
  @media (max-width: 768px) {
    position: relative;
    z-index: 999;
    background: none;
    padding-bottom: 0;
  }
`;

// Transcript bên phải 60%, scroll khi dài
const TranscriptWrapper = styled.div`
  display: flex;
  padding: 30px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 50px 20px 20px 0px;
  background: #FFF;
  color: #101828;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.1px;
  max-height: 100%;
  overflow-y: auto;
  margin: 30px;
  @media (max-width: 768px) {
    background: none;
    color: #FFF;
    text-align: center;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.1px;
    width: 100%;
    padding: 12px;
    border-radius: 0;
    margin: 0;
  }
`;


/* ─── Mic controls cố định đáy ───────────────────────────────────────────── */
const MicWrapper = styled.div`
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  pointer-events: auto;
  z-index: 999;
  @media (max-width: 768px) {
    position: relative;
    bottom: unset;
    top: unset;
    left: unset;
    right: unset;
    margin-bottom: 40px;
  }
`;

/* ─── Waves dưới cùng ─────────────────────────────────────────────────────── */
const WavesWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  pointer-events: none; /* để không chặn click mic */
  z-index: 10;
`;

export default function EngageChatVoice(props) {
  // props
  const { contentSpeechAudio: initialContentSpeechAudio, contentShowFirst } =
    props;
  const [audioFinished, setAudioFinished] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [statusGenerateAudio, setStatus] = useState(null);
  const [contentSpeechAudio, setContent] = useState(null);
  const [videoUrl, setVideoUrl] = useState(video1); // Default video URL
  const transcriptRef = useRef(null);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  
  useEffect(() => {
    // Use .mov video for Safari browser
    if (isSafari) {
      setVideoUrl(video1Mov);
    } else {
      setVideoUrl(video1); // Default to .webm for other browsers
    }
  }, [isSafari]);
  
  useEffect(() => {
    if (statusGenerateAudio === "playing" && initialContentSpeechAudio) {
      setContent(initialContentSpeechAudio);
      setVideoUrl(isSafari ? video2Mov : video2); // Change to video-2.mov if Safari is detected
    } else if (statusGenerateAudio !== "playing") {
      setVideoUrl(isSafari ? video1Mov : video1); // Reset to video-1.mov or .webm when no audio is playing
    }
  }, [statusGenerateAudio, initialContentSpeechAudio, isSafari]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [contentSpeechAudio, contentShowFirst]);

  const handleAudioStart = () => setAudioStarted(true);
  const handleAudioEnd = () => {
    setAudioFinished(true);
    setAudioStarted(false);
  };
  const handleStatusChange = (newStatus) => setStatus(newStatus);

  return (
    <FrameChatFullBox data-type="engage-voice">
      <ChatWrapper>
        <VideoWrapper>
          <ReactPlayer
            url={videoUrl}
            playing={true}
            controls={false}
            loop
            muted
            width="100%"
            height="100%"
            playsInline
            webkitPlaysInline
            style={{
              position: "absolute",
              top: 0,
              left: 0,
            }}
            config={{
              file: {
                attributes: {
                  style: {
                    objectFit: "cover", 
                    width: "100%",
                    height: "100%",
                  },
                },
              },
            }}
          />
        </VideoWrapper>
        {(contentSpeechAudio || contentShowFirst) &&
          <TranscriptWrapperContainer>
            <TranscriptWrapper ref={transcriptRef}>
              {contentSpeechAudio || contentShowFirst}
            </TranscriptWrapper>
          </TranscriptWrapperContainer>
        }
      </ChatWrapper>

      <MicWrapper>
        <WhisperSTTComponent
          {...props}
          audioFinished={audioFinished}
          audioStarted={audioStarted}
          statusGenerateAudio={statusGenerateAudio}
          onStatusChange={handleStatusChange}
        />
        <TextToSpeech
          {...props}
          onAudioStart={handleAudioStart}
          onAudioEnd={handleAudioEnd}
          onStatusChange={handleStatusChange}
        />
      </MicWrapper>

      <WavesWrapper>
        <Waves />
      </WavesWrapper>
    </FrameChatFullBox>
  );
}
